import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  Snackbar,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  ToggleOff as ToggleOffIcon,
  ToggleOn as ToggleOnIcon,
} from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  listarCandidatos,
  inativarCandidato,
  ativarCandidato,
  atualizarCandidato,
} from "../../services/candidato";
import { removerCandidaturasPorCandidato } from "../../services/candidato";
import {
  normalizarNome,
  normalizarEmail,
  normalizarTelefone,
} from "../../utils/normalization";

interface CandidatoLocal {
  id: number;
  nome: string;
  email: string;
  telefone: string;
  cpf: string;
  dataCadastro: string;
  status: "ativo" | "inativo";
}

const ListarCandidatos: React.FC = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [candidatos, setCandidatos] = useState<CandidatoLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState("");
  const [inactivateDialogOpen, setInactivateDialogOpen] = useState(false);
  const [candidatoParaInativar, setCandidatoParaInativar] =
    useState<CandidatoLocal | null>(null);
  const [inativando, setInativando] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [candidatoParaVisualizar, setCandidatoParaVisualizar] =
    useState<CandidatoLocal | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [candidatoParaEditar, setCandidatoParaEditar] =
    useState<CandidatoLocal | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("info");

  const showMessage = (
    message: string,
    severity: "success" | "error" | "warning" | "info",
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    carregarCandidatos();
  }, []);

  const carregarCandidatos = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listarCandidatos();
      // Ajustar conforme a estrutura da resposta da API
      const candidatosData = response.data || response;

      // Mapear os dados para o formato esperado
      const candidatosFormatados = candidatosData.map((candidato: any) => ({
        id: candidato.id,
        nome: candidato.nome,
        email: candidato.email,
        telefone: candidato.telefone || "",
        cpf: candidato.cpf,
        dataCadastro:
          candidato.dataCadastro ||
          candidato.createdAt ||
          new Date().toISOString(),
        status: candidato.status || "ativo",
      }));

      setCandidatos(candidatosFormatados);
    } catch (error) {
      console.error("Erro ao carregar candidatos:", error);
      setError("Erro ao carregar candidatos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleStatus = (candidato: CandidatoLocal) => {
    setCandidatoParaInativar(candidato);
    setInactivateDialogOpen(true);
  };

  const handleConfirmarToggleStatus = async () => {
    if (!candidatoParaInativar) return;

    const novoStatus =
      candidatoParaInativar.status === "ativo" ? "inativo" : "ativo";
    const acao = novoStatus === "ativo" ? "ativar" : "inativar";

    setInativando(true);
    try {
      // Se estiver inativando, tentar remover candidaturas primeiro (opcional)
      if (novoStatus === "inativo") {
        try {
          await removerCandidaturasPorCandidato(candidatoParaInativar.id);
        } catch (candidaturaError) {
          console.warn(
            "Aviso: Não foi possível processar candidaturas, mas continuando:",
            candidaturaError,
          );
          // Continua mesmo se houver erro - não é crítico
        }
      }

      await atualizarCandidato(candidatoParaInativar.id, {
        nome: candidatoParaInativar.nome,
        email: candidatoParaInativar.email,
        telefone: candidatoParaInativar.telefone,
        // Adicionar status como propriedade extra para sistemas que suportam
        ...({ status: novoStatus } as any),
      });

      // Atualizar o status do candidato na lista local
      setCandidatos((prev) =>
        prev.map((c) =>
          c.id === candidatoParaInativar.id
            ? { ...c, status: novoStatus as "ativo" | "inativo" }
            : c,
        ),
      );

      setInactivateDialogOpen(false);
      setCandidatoParaInativar(null);

      // Disparar evento personalizado para notificar outras partes do sistema
      const eventoTipo =
        novoStatus === "inativo" ? "candidatoInativado" : "candidatoAtivado";
      const evento = new CustomEvent(eventoTipo, {
        detail: {
          candidatoId: candidatoParaInativar.id,
          candidatoNome: candidatoParaInativar.nome,
          novoStatus: novoStatus,
        },
      });
      window.dispatchEvent(evento);

      showMessage(
        `Candidato ${acao === "ativar" ? "ativado" : "inativado"} com sucesso!`,
        "success",
      );
    } catch (error: any) {
      console.error(`Erro ao ${acao} candidato:`, error);
      let errorMessage = `Erro ao ${acao} candidato.`;

      if (error?.response?.status === 404) {
        errorMessage = "Candidato não encontrado.";
      } else if (error?.response?.status === 400) {
        errorMessage = `Não é possível ${acao}. Verifique os dados.`;
      } else if (error?.response?.status === 500) {
        errorMessage = "Erro no servidor. Tente novamente mais tarde.";
      }

      showMessage(errorMessage, "error");
      setError(errorMessage);
      setInactivateDialogOpen(false);
    } finally {
      setInativando(false);
    }
  };

  const handleCancelarInativacao = () => {
    setInactivateDialogOpen(false);
    setCandidatoParaInativar(null);
  };

  const handleVisualizarClick = (candidato: CandidatoLocal) => {
    setCandidatoParaVisualizar(candidato);
    setViewDialogOpen(true);
  };

  const handleFecharVisualizacao = () => {
    setViewDialogOpen(false);
    setCandidatoParaVisualizar(null);
  };

  const handleEditarClick = (candidato: CandidatoLocal) => {
    setCandidatoParaEditar({ ...candidato });
    setEditDialogOpen(true);
  };

  const handleFecharEdicao = () => {
    setEditDialogOpen(false);
    setCandidatoParaEditar(null);
  };

  const handleSalvarEdicao = async () => {
    if (!candidatoParaEditar) return;

    try {
      await atualizarCandidato(candidatoParaEditar.id, {
        nome: candidatoParaEditar.nome,
        email: candidatoParaEditar.email,
        telefone: candidatoParaEditar.telefone,
      });

      // Atualizar lista local
      setCandidatos((prev) =>
        prev.map((c) =>
          c.id === candidatoParaEditar.id ? candidatoParaEditar : c,
        ),
      );

      showMessage("Candidato atualizado com sucesso!", "success");
      handleFecharEdicao();
    } catch (error) {
      console.error("Erro ao atualizar candidato:", error);
      setError("Erro ao atualizar candidato. Tente novamente.");
    }
  };

  const filteredCandidatos = candidatos.filter((candidato) => {
    const matchesSearch =
      (candidato.nome || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidato.email || "").toLowerCase().includes(searchTerm.toLowerCase());

    // Sempre mostrar apenas candidatos ativos
    const matchesStatus = candidato.status === "ativo";

    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700 }}>
            Candidatos Cadastrados
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Total cadastrados: {candidatos.length}
          </Typography>
        </Box>

      </Box>

      <Paper elevation={3} sx={{ p: isMobile ? 2 : 3, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={isMobile ? "Buscar..." : "Buscar por nome ou email..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isMobile ? "small" : "medium"}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        // Visualização mobile em formato de cards
        <Box>
          {filteredCandidatos
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((candidato) => (
              <Card key={candidato.id} sx={{ mb: 2 }} elevation={3}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {normalizarNome(candidato.nome)}
                      </Typography>
                      <Stack spacing={1}>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {normalizarEmail(candidato.email)}
                        </Typography>
                        <Typography variant="body2">
                          {normalizarTelefone(candidato.telefone)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Cadastro:{" "}
                          {new Date(candidato.dataCadastro).toLocaleDateString(
                            "pt-BR",
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                  >
                    <Tooltip title="Visualizar">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleVisualizarClick(candidato)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
          <TablePagination
            component={Paper}
            elevation={3}
            sx={{ mt: 2 }}
            rowsPerPageOptions={[5, 10, 25]}
            count={filteredCandidatos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? "Por página:" : "Linhas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </Box>
      ) : (
        // Visualização desktop em tabela
        <TableContainer component={Paper} elevation={3}>
          <Table sx={{ minWidth: isTablet ? 500 : 650 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                {!isTablet && (
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                )}
                {!isTablet && (
                  <TableCell sx={{ fontWeight: 600 }}>Telefone</TableCell>
                )}
                <TableCell sx={{ fontWeight: 600 }}>Data Cadastro</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCandidatos
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((candidato) => (
                  <TableRow key={candidato.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {normalizarNome(candidato.nome)}
                        </Typography>
                        {isTablet && (
                          <Typography variant="caption" color="text.secondary">
                            {normalizarEmail(candidato.email)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {!isTablet && (
                      <TableCell>
                        {normalizarEmail(candidato.email)}
                      </TableCell>
                    )}
                    {!isTablet && (
                      <TableCell>
                        {normalizarTelefone(candidato.telefone)}
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(candidato.dataCadastro).toLocaleDateString(
                          "pt-BR",
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Visualizar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleVisualizarClick(candidato)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCandidatos.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isTablet ? "Por página:" : "Linhas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
          />
        </TableContainer>
      )}

      {/* Diálogo de confirmação de inativação */}
      <Dialog
        open={inactivateDialogOpen}
        onClose={handleCancelarInativacao}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {candidatoParaInativar?.status === "ativo"
            ? "Confirmar Inativação"
            : "Confirmar Ativação"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {candidatoParaInativar?.status === "ativo" ? (
              <>
                Tem certeza que deseja inativar o candidato{" "}
                <strong>{candidatoParaInativar?.nome}</strong>?
                <br />
                <br />
                <span style={{ color: "#f57c00", fontWeight: "bold" }}>
                  ⚠️ ATENÇÃO: Todas as candidaturas deste candidato serão
                  removidas do sistema e ele não poderá mais se candidatar a
                  novas vagas.
                </span>
                <br />
                <br />
                Os dados do candidato serão preservados e poderão ser reativados
                posteriormente.
              </>
            ) : (
              <>
                Tem certeza que deseja ativar o candidato{" "}
                <strong>{candidatoParaInativar?.nome}</strong>?
                <br />
                <br />O candidato voltará a ter acesso ao sistema e poderá se
                candidatar a vagas novamente.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelarInativacao} disabled={inativando}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarToggleStatus}
            color={
              candidatoParaInativar?.status === "ativo" ? "warning" : "success"
            }
            variant="contained"
            disabled={inativando}
            startIcon={
              inativando ? (
                <CircularProgress size={16} />
              ) : candidatoParaInativar?.status === "ativo" ? (
                <BlockIcon />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {inativando
              ? candidatoParaInativar?.status === "ativo"
                ? "Inativando..."
                : "Ativando..."
              : candidatoParaInativar?.status === "ativo"
              ? "Inativar"
              : "Ativar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleFecharVisualizacao}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Detalhes do Candidato</DialogTitle>
        <DialogContent>
          {candidatoParaVisualizar && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {normalizarNome(candidatoParaVisualizar.nome)}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Email:
                </Typography>
                <Typography variant="body1">
                  {normalizarEmail(candidatoParaVisualizar.email)}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Telefone:
                </Typography>
                <Typography variant="body1">
                  {candidatoParaVisualizar.telefone
                    ? normalizarTelefone(candidatoParaVisualizar.telefone)
                    : "Não informado"}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  CPF:
                </Typography>
                <Typography variant="body1">
                  {candidatoParaVisualizar.cpf && candidatoParaVisualizar.cpf.length === 11
                    ? candidatoParaVisualizar.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
                    : candidatoParaVisualizar.cpf}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Data de Cadastro:
                </Typography>
                <Typography variant="body1">
                  {new Date(
                    candidatoParaVisualizar.dataCadastro,
                  ).toLocaleDateString("pt-BR")}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharVisualizacao}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Edição */}
      <Dialog
        open={editDialogOpen}
        onClose={handleFecharEdicao}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Editar Candidato</DialogTitle>
        <DialogContent>
          {candidatoParaEditar && (
            <Box
              sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}
            >
              <TextField
                label="Nome"
                fullWidth
                value={candidatoParaEditar.nome}
                onChange={(e) =>
                  setCandidatoParaEditar({
                    ...candidatoParaEditar,
                    nome: e.target.value,
                  })
                }
              />

              <TextField
                label="Email"
                type="email"
                fullWidth
                value={candidatoParaEditar.email}
                onChange={(e) =>
                  setCandidatoParaEditar({
                    ...candidatoParaEditar,
                    email: e.target.value,
                  })
                }
              />

              <TextField
                label="Telefone"
                fullWidth
                value={candidatoParaEditar.telefone}
                onChange={(e) =>
                  setCandidatoParaEditar({
                    ...candidatoParaEditar,
                    telefone: e.target.value,
                  })
                }
              />

              <TextField
                label="CPF"
                fullWidth
                value={candidatoParaEditar.cpf}
                disabled
                helperText="CPF não pode ser alterado"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharEdicao}>Cancelar</Button>
          <Button onClick={handleSalvarEdicao} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListarCandidatos;
